import { API, authFetch } from "@/App";
import { responseError } from "./useFabricLibrary";

// Swatch photos are stored as small thumbnails: 400 px on the longest side is enough for the table and the printed sheet.
export const SWATCH_MAX_SIZE = 400;

export async function resizeImage(file, maxSize = SWATCH_MAX_SIZE, quality = 0.82) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error("resize");
  return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
}

// "docril_collection_stripes_00510_essential_sesane-olive.jpg" -> "00510", "01277-Docril_Fantasy.jpg" -> "01277"
export function codeFromFilename(name) {
  const base = String(name || "").split("/").pop().replace(/\.[^.]+$/, "");
  const tokens = base.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const digits = tokens.find((t) => /^\d{4,5}$/.test(t));
  if (digits) return digits;
  return (tokens.find((t) => /^(?=.*\d)[A-Za-z0-9]{4,5}$/.test(t)) || "").toUpperCase();
}

export const isImageFile = (file) => /^image\//.test(file.type) || /\.(jpe?g|png|webp|gif)$/i.test(file.name);

const readAllEntries = (reader) => new Promise((resolve) => {
  const out = [];
  const next = () => reader.readEntries((batch) => {
    if (!batch.length) resolve(out);
    else { out.push(...batch); next(); }
  }, () => resolve(out));
  next();
});

async function entryFiles(entry) {
  if (entry.isFile) return [await new Promise((resolve) => entry.file(resolve, () => resolve(null)))];
  if (entry.isDirectory) return (await Promise.all((await readAllEntries(entry.createReader())).map(entryFiles))).flat();
  return [];
}

// Must be called synchronously from the drop handler (dataTransfer items expire after the event).
export async function collectDroppedFiles(dataTransfer) {
  const entries = [...(dataTransfer.items || [])].map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.length) return [...(dataTransfer.files || [])];
  return (await Promise.all(entries.map(entryFiles))).flat().filter(Boolean);
}

export async function postSwatch(collection, code, file, { resize = true } = {}) {
  let upload = file;
  if (resize) {
    try { upload = await resizeImage(file); } catch { upload = file; }
  }
  const fd = new FormData();
  fd.append("collection", collection || "");
  fd.append("code", code);
  fd.append("file", upload);
  const res = await authFetch(`${API}/fabric-swatches`, { method: "POST", body: fd });
  if (!res.ok) throw new Error(await responseError(res, "Erreur d'ajout"));
  return res.json();
}
